import { atom, createStore } from "jotai";
import { atomFamily } from "jotai-family";
import { Observatory, Tag, ThemeTypeEnum } from "@/types";
import { atomWithToggle } from "@/utils";

/**
 * Store
 */
function createDevicesStore(
  initialObservatories: Observatory[],
  initialTags: Tag[],
  locale: string,
) {
  const store = createStore();

  const sortedObservatories = initialObservatories.toSorted((a, b) => {
    return a.name[locale]
      .toLowerCase()
      .localeCompare(b.name[locale].toLowerCase(), locale, {
        sensitivity: "base",
      });
  });

  store.set(observatoriesAtom, sortedObservatories);
  store.set(tagsAtom, initialTags);
  return store;
}

/**
 * Observatory Atoms
 */

const observatoriesAtom = atom<Observatory[]>([]);
const selectedObservatoryAtom = atom<Observatory | null>(null);
const filteredObservatoriesAtom = atom((get) => {
  const allObservatories = get(observatoriesAtom);
  const selectedTagIds = get(selectedTagIdsAtom);
  const themeVisibility = get(themeVisibilityAtom);

  let filteredObservatories = allObservatories;

  // Filter sites by tag
  if (selectedTagIds.length > 0) {
    filteredObservatories = filteredObservatories.filter((observatory) =>
      observatory.tags?.some((tag) => selectedTagIds.includes(tag.tagId)),
    );
  }

  filteredObservatories = filteredObservatories.filter(
    (observatory) => themeVisibility[observatory.theme as ThemeTypeEnum],
  );

  return filteredObservatories;
});

const earthObservatoriesAtom = atom((get) =>
  get(filteredObservatoriesAtom).filter(
    (observatory) => observatory.theme === ThemeTypeEnum.EARTH,
  ),
);

const waterObservatoriesAtom = atom((get) =>
  get(filteredObservatoriesAtom).filter(
    (observatory) => observatory.theme === ThemeTypeEnum.WATER,
  ),
);

const universeObservatoriesAtom = atom((get) =>
  get(filteredObservatoriesAtom).filter(
    (observatory) => observatory.theme === ThemeTypeEnum.UNIVERSE,
  ),
);

const biodiversityObservatoriesAtom = atom((get) =>
  get(filteredObservatoriesAtom).filter(
    (observatory) => observatory.theme === ThemeTypeEnum.BIODIVERSITY,
  ),
);

const observatoriesCountAtom = atom((get) => {
  const observatories = get(filteredObservatoriesAtom);
  const earthObservatories = get(earthObservatoriesAtom);
  const waterObservatories = get(waterObservatoriesAtom);
  const universeObservatories = get(universeObservatoriesAtom);
  const biodiversityObservatories = get(biodiversityObservatoriesAtom);

  const counters: {
    total: number;
    byTheme: Record<string, number>;
  } = {
    total: observatories.length,
    byTheme: {
      earth: earthObservatories.length,
      water: waterObservatories.length,
      universe: universeObservatories.length,
      biodiversity: biodiversityObservatories.length,
    },
  };

  return counters;
});

/**
 * Tag Atoms
 */

const tagsAtom = atom<Tag[]>([]);
const favouriteTagsAtom = atom((get) =>
  get(tagsAtom).filter((tag) => tag.isFavourite),
);
const selectedTagIdsAtom = atom<number[]>([]);
const selectedTagsAtom = atom((get) => {
  const allTags = get(tagsAtom);
  const selectedIds = get(selectedTagIdsAtom);

  return allTags.filter((tag) => selectedIds.includes(tag.id));
});

/**
 * Theme Atoms
 */
const themeKeys = [
  ThemeTypeEnum.EARTH,
  ThemeTypeEnum.WATER,
  ThemeTypeEnum.BIODIVERSITY,
  ThemeTypeEnum.UNIVERSE,
] as const;

const themeVisibilityAtomFamily = atomFamily(() => atomWithToggle(true));

const themeVisibilityAtom = atom((get) => {
  const values: Record<(typeof themeKeys)[number], boolean> = {} as Record<
    ThemeTypeEnum,
    boolean
  >;

  themeKeys.forEach((key) => {
    values[key] = get(themeVisibilityAtomFamily(key));
  });

  return values;
});

const sidebarFilterTagVisibilityAtom = atomWithToggle(true);

export {
  createDevicesStore,
  observatoriesAtom,
  selectedObservatoryAtom,
  filteredObservatoriesAtom,
  earthObservatoriesAtom,
  waterObservatoriesAtom,
  universeObservatoriesAtom,
  biodiversityObservatoriesAtom,
  observatoriesCountAtom,
  tagsAtom,
  favouriteTagsAtom,
  selectedTagIdsAtom,
  selectedTagsAtom,
  themeVisibilityAtomFamily,
  themeVisibilityAtom,
  sidebarFilterTagVisibilityAtom,
};
